/*
 * lnc-filters.js - 29Aug26
 *
 * Three fixes to collection/search filtering. None of them touch theme.min.js;
 * the theme keeps doing all the rendering and re-initialising (infinite scroll,
 * quick-add, hover images), so nothing downstream changes.
 *
 * 1. Debounce. The theme waits 1000ms after a filter click before it even
 *    starts the request. We shorten that one timer to 180ms, and only for
 *    timers started in the same tick as a filter change. If the theme ever
 *    changes its delay, the shortening stops applying - nothing breaks.
 *
 * 2. Races. The theme has no AbortController, so two quick clicks leave two
 *    requests running and whichever lands last wins - which is how you end up
 *    looking at results that do not match the boxes you ticked. We cancel the
 *    previous in-flight section request whenever a newer one starts.
 *
 * 3. Stuck loader. The theme's fetch has no .catch(), so one failed request
 *    leaves the spinner running and the grid hidden until a reload.
 *
 * Plus: the grid holds its height while loading, so the page stops collapsing
 * and snapping back under the filter panel.
 */
(function () {
  'use strict';

  var FILTER_FORM = '[data-filter-form]';
  var PARTIAL = '[data-partial]';
  var LOADING = '.collection__loading';
  var HIDE_CLASS = 'animation--collection-products-hide';
  var THEME_DELAY = 1000;
  var OUR_DELAY = 180;

  if (!document.querySelector(FILTER_FORM)) return;

  function partials() {
    return Array.prototype.slice.call(document.querySelectorAll(PARTIAL));
  }

  // Puts the grid back on screen if a request never completes.
  function recover() {
    partials().forEach(function (el) {
      el.classList.remove(HIDE_CLASS);
      el.style.minHeight = '';
    });
    Array.prototype.forEach.call(document.querySelectorAll(LOADING), function (el) {
      el.classList.remove('is-active');
    });
  }

  /* ---------------------------------------------------------------- 1 -- */
  // The theme's debounce is a plain setTimeout inside its change handler,
  // which runs in the bubble phase. We listen in the capture phase, swap in a
  // setTimeout that rewrites a 1000ms delay, and put the real one back on the
  // next macrotask - so only the theme's filter timer is affected.
  var nativeSetTimeout = window.setTimeout;
  var patched = false;

  function shortenNextTimer() {
    if (patched) return;
    patched = true;

    window.setTimeout = function (fn, delay) {
      var args = Array.prototype.slice.call(arguments, 2);
      var d = delay === THEME_DELAY ? OUR_DELAY : delay;
      return nativeSetTimeout.apply(window, [fn, d].concat(args));
    };

    nativeSetTimeout(function () {
      window.setTimeout = nativeSetTimeout;
      patched = false;
    }, 0);
  }

  function isFilterControl(el) {
    return !!(el && el.closest && el.closest(FILTER_FORM));
  }

  // Hold the grid's current height so the page does not collapse mid-request.
  function holdHeight() {
    partials().forEach(function (el) {
      if (!el.style.minHeight) {
        var h = el.getBoundingClientRect().height;
        if (h > 0) el.style.minHeight = Math.round(h) + 'px';
      }
    });
  }

  document.addEventListener('change', function (e) {
    if (!isFilterControl(e.target)) return;
    shortenNextTimer();
    holdHeight();
  }, true);

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('[data-remove-filter], [data-remove-range], [data-button="clear-all"]')) {
      holdHeight();
    }
  }, true);

  // Release the reserved height once the grid has new contents.
  var release = null;
  if (window.MutationObserver) {
    release = new MutationObserver(function () {
      nativeSetTimeout(function () {
        partials().forEach(function (el) { el.style.minHeight = ''; });
      }, 60);
    });
    partials().forEach(function (el) {
      release.observe(el, { childList: true });
    });
  }

  /* ------------------------------------------------------------ 2 and 3 -- */
  var nativeFetch = window.fetch;
  if (typeof nativeFetch !== 'function' || typeof window.AbortController !== 'function') return;

  var inFlight = null;

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';

    if (url.indexOf('section_id=') === -1) {
      return nativeFetch.apply(window, arguments);
    }

    if (inFlight) {
      try { inFlight.abort(); } catch (err) { /* already settled */ }
    }

    var controller = new AbortController();
    inFlight = controller;

    var opts = {};
    if (init) { for (var k in init) { if (Object.prototype.hasOwnProperty.call(init, k)) opts[k] = init[k]; } }
    opts.signal = controller.signal;

    return nativeFetch.call(window, input, opts).then(function (res) {
      if (inFlight === controller) inFlight = null;
      return res;
    }).catch(function (err) {
      if (inFlight === controller) inFlight = null;

      // A superseded request must not resolve, or the theme would render its
      // stale HTML over the newer results. A pending promise ends the chain
      // quietly; the newer request owns the UI from here.
      if (err && err.name === 'AbortError') {
        return new Promise(function () {});
      }

      recover();
      return new Promise(function () {});
    });
  };
})();
